// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module validators
 * @package @geenius/analytics
 * @description Defines the shared Zod validators used by analytics runtime,
 * privacy, and dashboard helpers. These schemas keep runtime validation aligned
 * with the public TypeScript contract exported by the shared package.
 */

import { z } from "zod";

/**
 * Runtime validator for collected client device metadata.
 */
export const DeviceInfoSchema = z.object({
  browser: z.string(),
  os: z.string(),
  device: z.enum(["desktop", "tablet", "mobile"]),
  screenWidth: z.number(),
  screenHeight: z.number(),
  language: z.string(),
});

/**
 * Runtime validator for identified user traits.
 */
export const UserTraitsSchema = z.looseObject({
  email: z.email().optional(),
  name: z.string().optional(),
  plan: z.string().optional(),
  company: z.string().optional(),
});

const AnalyticsEventTraitsSchema = z.looseObject({
  email: z.string().optional(),
  name: z.string().optional(),
  plan: z.string().optional(),
  company: z.string().optional(),
});

/**
 * Runtime validator for tracked analytics events.
 */
export const AnalyticsEventSchema = z.object({
  id: z.string(),
  type: z.enum(["page_view", "click", "form_submit", "custom"]),
  name: z.string().min(1),
  properties: z.record(z.string(), z.unknown()).optional(),
  url: z.string(),
  referrer: z.string(),
  ipAddress: z.string().optional(),
  sessionId: z.string(),
  userId: z.string().optional(),
  traits: AnalyticsEventTraitsSchema.optional(),
  device: DeviceInfoSchema,
  timestamp: z.number(),
});

/**
 * Runtime validator for tracked page-view payloads.
 */
export const PageViewSchema = z.object({
  url: z.string().min(1),
  title: z.string(),
  referrer: z.string(),
  properties: z.record(z.string(), z.unknown()).optional(),
  duration: z.number().optional(),
  ipAddress: z.string().optional(),
  sessionId: z.string(),
  userId: z.string().optional(),
  timestamp: z.number(),
});

/**
 * Runtime validator for analytics consent choices.
 */
export const ConsentSchema = z
  .object({
    essential: z.boolean(),
    analytics: z.boolean(),
    marketing: z.boolean(),
    personalization: z.boolean().optional(),
    functional: z.boolean().optional(),
    timestamp: z.number(),
  })
  .transform((settings) => {
    const personalization =
      settings.personalization ?? settings.functional ?? false;
    return {
      ...settings,
      personalization,
      functional: settings.functional ?? personalization,
    };
  });

/**
 * Runtime validator for funnel step definitions.
 */
export const FunnelStepSchema = z.object({
  name: z.string(),
  event: z.string(),
  properties: z.record(z.string(), z.unknown()).optional(),
});

/**
 * Runtime validator for provider configuration.
 */
export const ProviderConfigSchema = z.object({
  provider: z.enum(["posthog", "mixpanel", "google-analytics", "plausible"]),
  apiKey: z.string().min(1),
  host: z.string().optional(),
  enabled: z.boolean().optional(),
});

/**
 * Runtime validator for numeric time ranges.
 */
export const TimeRangeSchema = z.object({
  start: z.number(),
  end: z.number(),
});

/**
 * Runtime validator for privacy configuration.
 */
export const PrivacySettingsSchema = z.object({
  mode: z.enum(["mask", "anonymize", "redact"]),
  maskedFields: z.array(z.string()),
  anonymizeIdentifiers: z.boolean(),
});

/**
 * Runtime validator for cohort retention data points.
 */
export const CohortRetentionPointSchema = z.object({
  day: z.number(),
  retainedUsers: z.number(),
  retentionRate: z.number(),
});

/**
 * Runtime validator for cohort buckets.
 */
export const CohortBucketSchema = z.object({
  cohort: z.string(),
  size: z.number(),
  retained: z.array(CohortRetentionPointSchema),
  lastActivity: z.number(),
});

/**
 * Runtime validator for full cohort analyses.
 */
export const CohortAnalysisSchema = z.object({
  name: z.string(),
  cohortType: z.enum(["user", "session"]),
  intervals: z.array(z.number()),
  cohorts: z.array(CohortBucketSchema),
  generatedAt: z.number(),
});

/**
 * Runtime validator for individual session replay frames.
 */
export const SessionReplayFrameSchema = z.object({
  id: z.string(),
  kind: z.enum(["event", "page_view"]),
  label: z.string(),
  url: z.string(),
  timestamp: z.number(),
  deltaMs: z.number(),
  sessionId: z.string(),
  userId: z.string().optional(),
  properties: z.record(z.string(), z.unknown()).optional(),
});

/**
 * Runtime validator for full session replay snapshots.
 */
export const SessionReplaySnapshotSchema = z.object({
  sessionId: z.string(),
  startedAt: z.number(),
  endedAt: z.number(),
  duration: z.number(),
  frames: z.array(SessionReplayFrameSchema),
  activePages: z.array(z.string()),
  userId: z.string().optional(),
});

/**
 * Shared event payload inferred from the runtime event validator.
 */
export type ValidatedEvent = z.infer<typeof AnalyticsEventSchema>;

/**
 * Shared page-view payload inferred from the runtime page-view validator.
 */
export type ValidatedPageView = z.infer<typeof PageViewSchema>;
